import { BlockKind, ImageBlock } from "../types";

const ZOETIS_IMAGE: ImageBlock = {
  kind: BlockKind.Image,
  alt: "Zoetis",
  source:
    "https://media.ourvet.com/product-content/vendor_programs/zoetis_petcare_rewards/hero-image.png",
};

export const ZOETIS_CONTENT = {
  header: "Earn more with Zoetis ==*Petcare Rewards*==",
  visual: ZOETIS_IMAGE,
  bullets: [
    {
      label: "Earn Points",
      description:
        "You'll earn points with every eligible product purchase you submit within 60 days.",
    },
    {
      label: "Redeem for Rewards",
      description:
        "For every 10 points you earn with eligible product purchases, you'll get $1 in rewards to spend on future vet visits.",
    },
    {
      label: "Spend rewards on veterinary care",
      description: "Use your rewards card to help pay for any product or service from your clinic.",
    },
  ],
  action: {
    kind: "zoetis_petcare_actions",
    url: "https://www.zoetispetcare.com/rewards",
    label: "Enroll in Petcare Rewards",
  },
  faqs: [
    {
      question: "How do I signup for Rewards?",
      answer:
        "You can join Zoetis Petcare Rewards by creating an account online at **www.zoetispetcare.com/register** or through Zoetis' fully featured pet care app, **The Pack**, available on iOS and Android.",
    },
    {
      question: "How does Petcare Rewards work?",
      answer:
        "Zoetis Petcare Rewards is a program designed to help keep your pets at their best. By purchasing eligible Zoetis products, you'll earn points that can be redeemed for a variety of amazing Rewards, including a Petcare Rewards Card to spend on products or services at your veterinarian. As you earn more points, you can reach new tiers that unlock unique perks and extra ways to earn. For more information or to sign up, check out the Petcare Rewards page at **www.zoetispetcare.com**.",
    },
    {
      question: "How do I earn points?",
      answer:
        "Upload a photo of your invoice online at **zoetispetcare.com** or in the app, **The Pack**, within 30 days of purchasing qualified Zoetis products. All eligible products from a single invoice should be submitted at the same time. The Petcare Rewards team will review your submission and either approve or deny your submission within five to seven business days.",
    },
    {
      question: "How many points can I earn?",
      answer:
        "The points you can earn will vary based on the product and the amount you purchase. Check the Ways to Earn page at **www.zoetispetcare.com** for the exact point values of each product. You can also earn points on special promotional offers as they become available. As you level up to higher tiers, you'll earn more points per submission based on your tier multiplier.\n10 points = $1 in value when redeemed in the Rewards Store.",
    },
    {
      question: "What should I capture in my invoice submission image?",
      answer:
        "When submitting your invoice, make sure the image clearly includes:\n- Date of purchase\n- Your name\n- Your pet's name(s)\n- Product name(s) as well as quantity and dosage\n\nAll eligible products from a single invoice should be submitted at the same time. You can submit multiple images for the same invoice if needed.",
    },
    {
      question: "How do I know that my submission was received?",
      answer:
        "You'll receive an email confirmation after submitting your invoice. Your submission status can also be checked in the **Activity** section when logged in at **www.zoetispetcare.com** or in the app, **The Pack**. You'll receive an email notification once your submission is approved.",
    },
    {
      question: "Can invoice submissions be denied?",
      answer:
        "An invoice submission can be denied for a few different reasons:\n\n- The invoice image you provided was not clear enough or was missing necessary purchase details.\n- The product quantity or dosage you entered did not match what was shown on your invoice.\n- Your invoice wasn't submitted within 30 days of your purchase date.\n- Multiple invoices were submitted. Invoices for purchases made on different dates must be submitted separately.\n- If your invoice was denied, use this list to make sure you resubmit correctly!",
    },
    {
      question: "How do Tiers work?",
      answer:
        "Zoetis Petcare Rewards has three tiers: Bronze, Silver, and Gold. All new members start at Bronze and can progress to higher tiers by earning points. Silver and Gold tiers offer point multipliers (and other fun perks) that make it easier to earn even more points on each submission.",
    },
    {
      question: "What is a Membership Year?",
      answer:
        "Your Membership Year is unique to you and begins the day you sign up, resetting annually on that date each following year. During each year, you can reach higher tiers by earning the required amount of Tier Points (which are earned via approved invoice submissions). If you level up to Silver or Gold Tier, you'll retain it for the remainder of your current Membership Year and the following Membership Year, giving you plenty of time to enjoy its point multipliers and other perks.\n\nOnce a membership year ends, however, your Tier Points will reset, and you'll start from zero at the beginning of your next year.",
    },
    {
      question: "What are Tier Points?",
      answer:
        "Every time you earn points, they're applied towards your Tier Points total which accrues over the course of your membership year. Once you earn enough Tier Points for a new tier, you'll automatically level up and be notified. At the start of a new Membership Year, Tier Points reset to zero - so make sure you monitor your progress before a year ends!\n\nTier Points don't affect your redeemable points balance (which does not reset each year), and redeeming points in the Rewards store won't affect your Tier Points total.",
    },
    {
      question: "How many tier points do I need for each tier?",
      answer:
        "- Bronze: 0-324 Tier Points\n- Silver: 325-649 Tier Points\n- Gold: 650+ Tier Points\n\nTier Points reset at the end of each Membership Year.",
    },
    {
      question: "What are the perks for each Tier?",
      answer:
        "You can check out the perks for each tier **www.zoetispetcare.com/rewards#rewards-tiers**",
    },
    {
      question: "Do points expire?",
      answer:
        "Yes, Redeemable Points expire 15 months after they're earned for Bronze and Silver tier members. Points earned as a Gold member do not expire. You will receive reminders to redeem your points before they expire.",
    },
    {
      question: "How many points do I need to earn a Petcare Rewards Card?",
      answer:
        "You need 100 Redeemable Points for a Petcare Rewards Card. After you receive it, you can continue to reload it for 100 points or more as many times as you like.",
    },
    {
      question: "How does my Zoetis Petcare Rewards Card work?",
      answer:
        "Your Zoetis Petcare Rewards Card can be used at any veterinary practice in the U.S. to help pay for any product or service they offer, even non-Zoetis products. You can reload your Petcare Rewards Card with more funds as you earn more points.\n\nOnce your Petcare Rewards Card has shipped or funds have been loaded, you'll receive a confirmation email. Please allow two to three weeks for your card to be delivered.",
    },
    {
      question: "When will my Rewards Store items ship?",
      answer:
        "Expect any items redeemed in the Rewards Store to take two to three weeks to arrive.",
    },
    {
      question: "What is the return policy for Rewards Store items?",
      answer:
        "Returns are eligible for damaged or defective items within 30 days of receiving them. All other sales are final.\n\nIf you have a question or would like to initiate a return, please contact Zoetis Petcare customer support at **www.zoetispetcare.com/contact**",
    },
    {
      question: "Does my Zoetis Petcare Rewards Card expire?",
      answer:
        "Your Petcare Rewards Card is valid for three years from the date it's issued. The expiration date is printed on the card, so it's easy to remember. Once it expires, any remaining funds left on the card will be lost. You'll receive reminders before your card expires to spend any remaining funds at your vet, and it's easy to redeem for a new card when your old one expires.",
    },
    {
      question: "Can I transfer my points to another person?",
      answer:
        "Points are non-transferable and may not be sold or used as a gift to another member.",
    },
    {
      question: "Can money on my Rewards Card be turned back into Redeemable Points?",
      answer:
        "Once your points are redeemed to a Petcare Rewards Card, they can no longer be used in the Rewards Store or donated online.",
    },
    {
      question: "Who is eligible to participate in Petcare Rewards?",
      answer:
        "Zoetis Petcare Rewards is open to U.S. and U.S. territory residents who are at least 18 years old.",
    },
  ],
  products: {
    header: "Browse eligible zoetis petcare rewards products",
    subheader:
      "Help pay for future veterinary care with additional rewards. Protect your dog and **earn up to $45** in rewards.",
  },
};
